import { useState, useRef, useEffect } from "react";

/**
 * Tracks which field is focused inside a container by delegating
 * focusin/focusout events and reading `data-hint-field` attributes.
 *
 * Returns `{ focusedField, containerRef }` where `focusedField` is the
 * string value of the closest `data-hint-field` attribute (or null).
 *
 * An 80ms debounce on blur prevents flicker when focus moves between
 * sibling elements within the same field (e.g. tiptap toolbar → editor).
 */
export function useFieldFocus() {
  const [focusedField, setFocusedField] = useState(null);
  const containerRef = useRef(null);
  const blurTimeoutRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleFocusIn = (e) => {
      clearTimeout(blurTimeoutRef.current);
      const fieldEl = e.target.closest("[data-hint-field]");
      setFocusedField(fieldEl ? fieldEl.dataset.hintField : null);
    };

    const handleFocusOut = () => {
      blurTimeoutRef.current = setTimeout(() => {
        if (
          !container.contains(document.activeElement) ||
          !document.activeElement.closest("[data-hint-field]")
        ) {
          setFocusedField(null);
        } else {
          const fieldEl = document.activeElement.closest(
            "[data-hint-field]",
          );
          setFocusedField(fieldEl ? fieldEl.dataset.hintField : null);
        }
      }, 80);
    };

    container.addEventListener("focusin", handleFocusIn);
    container.addEventListener("focusout", handleFocusOut);

    return () => {
      clearTimeout(blurTimeoutRef.current);
      container.removeEventListener("focusin", handleFocusIn);
      container.removeEventListener("focusout", handleFocusOut);
    };
  }, []);

  return { focusedField, containerRef };
}
